import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

import EditConversationPopup from './popups/EditConversationPopup';
import DeleteConversationPopup from './popups/DeleteConversationPopup';
import { useTranslation } from '../hooks/useTranslation';
import { authFetch } from '../utils/auth';

const ConversationDropdown = ({ conversationId, onClose, onCollectionUpdated, onUpdateCollection, onConversationDeleted }) => {
  const [collections, setCollections] = useState([]);
  const [showCollections, setShowCollections] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await authFetch('/api/collections');
        if (response.ok) {
          const data = await response.json();
          setCollections(data);
        } else {
          console.error("Failed to fetch collections");
        }
      } catch (error) {
        console.error("Error fetching collections:", error);
      }
    };

    fetchCollections();
  }, []);

  const handleAddToCollection = async (collectionId) => {
    try {
      const response = await authFetch(`/collections/${collectionId}/conversations/${conversationId}`, {
        method: 'POST',
      });
      if (response.ok) {
        const updatedCollection = await response.json();
        onCollectionUpdated(updatedCollection);
        onUpdateCollection(updatedCollection);
        onClose();
      } else {
        console.error('Failed to add conversation to collection');
      }
    } catch (error) {
      console.error('Error adding conversation to collection:', error);
    }
  };

  const handleEditTitle = () => {
    setShowEditPopup(true);
  };

  const handleDeleteConversation = () => {
    setShowDeletePopup(true);
  };

  const handleOutsideClick = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  return (
    <>
      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10" ref={dropdownRef}>
        <div className="py-1">
          <button
            onClick={() => setShowCollections(!showCollections)}
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {t('conversationDropdown.addToCollection')}
          </button>
          {showCollections && (
            <div className="pl-4">
              {collections.map(collection => (
                <button
                  key={collection._id}
                  onClick={() => handleAddToCollection(collection._id)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {collection.title}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={handleEditTitle}
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
          >
            <FontAwesomeIcon icon={faEdit} className="mr-2" />
            {t('conversationDropdown.editTitle')}
          </button>
          <button
            onClick={handleDeleteConversation}
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2" />
            {t('conversationDropdown.deleteConversation')}
          </button>
        </div>
      </div>
      {showEditPopup && (
        <EditConversationPopup
          conversationId={conversationId}
          onClose={() => setShowEditPopup(false)}
          onUpdateConversation={(updatedConversation) => {
            onUpdateCollection(updatedConversation);
            setShowEditPopup(false);
            onClose();
          }}
        />
      )}
      {showDeletePopup && (
        <DeleteConversationPopup
          onConfirm={async () => {
            try {
              const response = await authFetch(`/api/conversations/${conversationId}`, {
                method: 'DELETE',
              });
              if (response.ok) {
                await onConversationDeleted(conversationId);
                setShowDeletePopup(false);
                onClose();
              } else {
                console.error('Failed to delete conversation');
              }
            } catch (error) {
              console.error('Error deleting conversation:', error);
            }
          }}
          onCancel={() => setShowDeletePopup(false)}
        />
      )}
    </>
  );
};

export default ConversationDropdown;
